import React, { useEffect, useMemo, useState } from "react";

import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DemoLogin from "./pages/DemoLogin";
import ClientDashboard from "./pages/ClientDashboard";
import ClientCaseDetail from "./pages/ClientCaseDetail";
import StaffDashboard from "./pages/StaffDashboard";
import StaffCaseDetail from "./pages/StaffCaseDetail";
import StaffClientsPage from "./pages/StaffClientsPage";
import StaffDocumentsPage from "./pages/StaffDocumentsPage";
import FirmSettings from "./pages/FirmSettings";
import StaffAccount from "./pages/StaffAccount";
import ClientAccount from "./pages/ClientAccount";
import GHLOAuthCallback from "./pages/GHLOAuthCallback";
import SsoLaunch from "./pages/SsoLaunch";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ProtectedRoute from "./components/ProtectedRoute";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { TenantProvider, useTenant } from "./context/TenantContext";

import { UserRole, UserProfile } from "./types";
import { performNavigate, AppView } from "./routeMap";

const VALID_VIEWS: AppView[] = ["dashboard", "clients", "case_detail", "documents", "settings", "account"];

const FullScreenSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
  </div>
);

const EmptyState: React.FC<{ title: string; message: string; actionText?: string; onAction?: () => void }> = ({
  title,
  message,
  actionText,
  onAction,
}) => (
  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-10 text-center">
    <div className="text-3xl mb-4">📄</div>
    <h2 className="text-xl font-black text-slate-800 mb-2">{title}</h2>
    <p className="text-sm text-slate-500 mb-6">{message}</p>
    {actionText && onAction && (
      <button onClick={onAction} className="px-6 py-3 rounded-xl bg-brand-800 text-white font-bold">
        {actionText}
      </button>
    )}
  </div>
);

const AccessDenied: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div className="bg-white rounded-3xl shadow-sm border border-red-100 p-10 text-center">
    <div className="text-3xl mb-4">⛔</div>
    <h2 className="text-xl font-black text-slate-800 mb-2">Access restricted</h2>
    <p className="text-sm text-slate-500 mb-6">You do not have permission to view this section for this firm.</p>
    <button onClick={onBack} className="px-6 py-3 rounded-xl bg-slate-100 text-slate-700 font-bold">
      Back to Dashboard
    </button>
  </div>
);

const isValidView = (v: string | null | undefined): v is AppView => {
  return !!v && (VALID_VIEWS as string[]).includes(v);
};

const getPathSegments = () => window.location.pathname.split("/").filter(Boolean);

const AuthenticatedApp: React.FC = () => {
  const { userDoc, logout, isStaff, firmId, staffRole, isFirmOwner } = useAuth();

  const userProfile: UserProfile | null = useMemo(() => {
    if (!userDoc) return null;
    return {
      ...userDoc,
      role: userDoc.role as UserRole,
      createdAt: (userDoc as any).createdAt?.toDate?.().toISOString() || new Date().toISOString(),
    };
  }, [userDoc]);

  const getViewFromUrl = (): AppView => {
    const segments = getPathSegments();
    if (segments[0] === "location" && segments[2]) {
      return isValidView(segments[2]) ? (segments[2] as AppView) : "dashboard";
    }
    if (segments[0] && isValidView(segments[0])) return segments[0] as AppView;
    return "dashboard";
  };

  const [currentView, setCurrentView] = useState<AppView>(getViewFromUrl);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);

  useEffect(() => {
    const sync = () => setCurrentView(getViewFromUrl());
    window.addEventListener("popstate", sync);
    window.addEventListener("filershub:navigate", sync as any);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("filershub:navigate", sync as any);
    };
  }, []);

  const navigateTo = (view: AppView, source: string, caseId: string | null = null) => {
    performNavigate(
      setCurrentView,
      view,
      userDoc?.role || "NONE",
      source,
      () => {
        if (caseId) setSelectedCaseId(caseId);
        else if (view !== "case_detail") setSelectedCaseId(null);
      },
      firmId
    );
  };

  const handleCaseSelect = (caseId: string) => {
    setSelectedCaseId(caseId);
    navigateTo("case_detail", "CaseSelect", caseId);
  };

  // HYDRATION FIX: Prevent FullScreenSpinner when switching tabs in a hydrated app
  const isHydrated = !!(userProfile && firmId);
  const isDemo = sessionStorage.getItem("FILERSHUB_DEMO_SESSION") !== null;

  if (!isHydrated && !isDemo) {
    return <FullScreenSpinner />;
  }

  const canSeeSettings = isFirmOwner || staffRole === "Manager";

  return (
    <Layout
      user={(userProfile || ({} as any)) as any}
      currentView={currentView}
      onNavigate={(view) => navigateTo(view as AppView, "Layout.NavItem")}
      onLogout={logout}
    >
      {currentView === "dashboard" && isStaff && (
        <StaffDashboard
          onCaseSelect={handleCaseSelect}
          onNavigate={(v) => navigateTo(v as AppView, "StaffDashboard.Action")}
        />
      )}
      {currentView === "dashboard" && !isStaff && (
        <ClientDashboard
          onCaseSelect={handleCaseSelect}
          onNavigate={(v) => navigateTo(v as AppView, "ClientDashboard.Action")}
        />
      )}

      {currentView === "clients" && isStaff && <StaffClientsPage onCaseSelect={handleCaseSelect} />}

      {currentView === "case_detail" && selectedCaseId && !isStaff && (
        <ClientCaseDetail
          caseId={selectedCaseId}
          firmId={firmId || ""}
          onBack={() => navigateTo("dashboard", "CaseDetail.Back")}
        />
      )}

      {currentView === "case_detail" && selectedCaseId && isStaff && (
        <StaffCaseDetail
          caseId={selectedCaseId}
          firmId={firmId || ""}
          onBack={() => navigateTo("clients", "CaseDetail.Back")}
        />
      )}

      {currentView === "documents" && isStaff && <StaffDocumentsPage />}
      {currentView === "documents" && !isStaff && (
        <EmptyState
          title="No documents yet"
          message="When your firm uploads or requests documents, you will see them here."
          actionText="Back to Dashboard"
          onAction={() => navigateTo("dashboard", "ClientDocs.Back")}
        />
      )}

      {currentView === "settings" && canSeeSettings && <FirmSettings />}
      {currentView === "settings" && !canSeeSettings && (
        <AccessDenied onBack={() => navigateTo("dashboard", "Settings.DeniedBack")} />
      )}

      {currentView === "account" && isStaff && <StaffAccount />}
      {currentView === "account" && !isStaff && <ClientAccount />}
    </Layout>
  );
};

const DEMO_FIRM_ID = "hannah-financial-demo";

const safeSessionSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};

const isDemoPath = (path: string) => path.toLowerCase().includes(`/location/${DEMO_FIRM_ID}/`);

const AppRoutes = () => {
  const { user, loading: authLoading, userDoc, appUser } = useAuth();
  const { loading: tenantLoading } = useTenant();

  const [currentPath, setCurrentPath] = useState(window.location.pathname.toLowerCase());

  useEffect(() => {
    const sync = () => setCurrentPath(window.location.pathname.toLowerCase());
    window.addEventListener("popstate", sync);
    window.addEventListener("filershub:navigate", sync as any);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("filershub:navigate", sync as any);
    };
  }, []);

  // Always allow legal pages
  if (currentPath.startsWith("/privacy")) return <PrivacyPolicy />;
  if (currentPath.startsWith("/terms")) return <TermsOfService />;

  // ✅ DEMO: do not let SSO or auth gating block demo firm routes
  if (isDemoPath(currentPath)) {
    safeSessionSet("FILERSHUB_SSO_LOC", DEMO_FIRM_ID);

    const hasDemoSession = sessionStorage.getItem("FILERSHUB_DEMO_SESSION") !== null;
    if (!hasDemoSession) return <DemoLogin />;

    // For demo we intentionally bypass Firebase auth + ProtectedRoute
    return <AuthenticatedApp />;
  }

  if (tenantLoading || authLoading) return <FullScreenSpinner />;

  if (currentPath.includes("/demo-login")) return <DemoLogin />;
  if (currentPath.includes("/sso/launch") || currentPath.includes("/crm-sso")) return <SsoLaunch />;
  if (currentPath.includes("/crm-callback")) return <GHLOAuthCallback />;

  if (!user) return <LoginPage />;
  if (!userDoc && !appUser) return <FullScreenSpinner />;

  return (
    <ProtectedRoute allowedRoles={Object.values(UserRole)}>
      <AuthenticatedApp />
    </ProtectedRoute>
  );
};

const App: React.FC = () => (
  <TenantProvider>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </TenantProvider>
);

export default App;
